import { useMemo, useRef, useState } from "react";
import { Badge, Button, Checkbox, Space, Spin, Table, Typography } from "antd";
import { ArrowRightOutlined, CheckOutlined } from "@ant-design/icons";
import type { SuggestedSocketMapping } from "./useSuggestedMappings";
import { arraysEqual } from "./socketToAssetMapUtils";

type SuggestedMappingsSectionProps = {
  suggestedMappings: SuggestedSocketMapping[];
  connectedSocketIds: Set<string>;
  isLoading: boolean;
  isLoadingSockets: boolean;
  onApply: (socketId: string, assetIds: string[]) => void;
};

export function SuggestedMappingsSection({
  suggestedMappings,
  connectedSocketIds,
  isLoading,
  isLoadingSockets,
  onApply,
}: SuggestedMappingsSectionProps) {
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<string>>(
    () => new Set(),
  );

  // Keep selection in sync: auto-select new 100% suggestions, prune stale ones
  const suggestionSocketIds = useMemo(
    () => suggestedMappings.map((s) => s.socketId),
    [suggestedMappings],
  );
  const prevSuggestionIdsRef = useRef<string[]>([]);
  if (!arraysEqual(prevSuggestionIdsRef.current, suggestionSocketIds)) {
    const prevIds = new Set(prevSuggestionIdsRef.current);
    prevSuggestionIdsRef.current = suggestionSocketIds;
    setSelectedSuggestions((prev) => {
      const next = new Set<string>();
      for (const id of suggestionSocketIds) {
        if (prevIds.has(id)) {
          // Existing suggestion: keep user's choice
          if (prev.has(id)) {
            next.add(id);
          }
        } else {
          // New suggestion: only auto-select if every entry is 100% confidence
          const suggestion = suggestedMappings.find((s) => s.socketId === id);
          if (
            suggestion &&
            suggestion.entries.every((e) => e.confidence === 1.0)
          ) {
            next.add(id);
          }
        }
      }
      return next;
    });
  }

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-1">
        <Typography.Title level={5} className="!mb-0 !text-gray-900">
          Suggested Mappings
        </Typography.Title>
        {suggestedMappings.length > 0 && (
          <Button
            type="primary"
            icon={<CheckOutlined />}
            disabled={selectedSuggestions.size === 0}
            onClick={() => {
              for (const s of suggestedMappings) {
                if (selectedSuggestions.has(s.socketId)) {
                  onApply(
                    s.socketId,
                    s.entries.map((e) => e.assetId),
                  );
                }
              }
            }}
          >
            Apply Selected
          </Button>
        )}
      </div>
      <Typography.Text type="secondary" className="text-xs">
        Based on transaction timing correlations from recent transactions.
      </Typography.Text>
      {isLoading ? (
        <div className="mt-3">
          <Space>
            <Spin size="small" />
            <Typography.Text type="secondary" className="text-sm">
              Loading suggestions…
            </Typography.Text>
          </Space>
        </div>
      ) : suggestedMappings.length === 0 ? (
        <div className="mt-3">
          <Typography.Text type="secondary" className="text-sm italic">
            No suggestions available — not enough recent transaction data to
            correlate sockets with assets.
          </Typography.Text>
        </div>
      ) : (
        <Table
          className="mt-4"
          dataSource={suggestedMappings}
          rowKey="socketId"
          pagination={false}
          size="small"
          columns={[
            {
              title: "OCPP Socket ID",
              dataIndex: "socketId",
              key: "socketId",
              width: 240,
              render: (socketId: string) => (
                <Space size="middle">
                  {isLoadingSockets ? (
                    <Spin size="small" />
                  ) : (
                    <Badge
                      status={
                        connectedSocketIds.has(socketId) ? "success" : "default"
                      }
                    />
                  )}
                  <Typography.Text strong className="text-sm">
                    {socketId}
                  </Typography.Text>
                </Space>
              ),
            },
            {
              title: "Suggested Connector / EVSE Mapping",
              dataIndex: "entries",
              key: "entries",
              render: (
                entries: {
                  connectorId: number;
                  assetId: string;
                  confidence: number;
                  nbMatchingTransactions: number;
                  nbConsideredTransactions: number;
                }[],
              ) => (
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {entries.map((entry) => (
                    <span
                      key={entry.connectorId}
                      className="inline-flex items-center gap-1.5 text-xs"
                    >
                      <span className="font-mono text-[11px] text-gray-400">
                        #{entry.connectorId}
                      </span>
                      <ArrowRightOutlined className="text-[10px] text-gray-300" />
                      <span className="text-gray-700 font-medium">
                        {entry.assetId}
                      </span>
                      <span className="text-primary-600 text-[11px]">
                        ({entry.nbMatchingTransactions}/
                        {entry.nbConsideredTransactions},{" "}
                        {Math.round(entry.confidence * 100)}%)
                      </span>
                    </span>
                  ))}
                </div>
              ),
            },
            {
              title: () => (
                <Checkbox
                  indeterminate={
                    selectedSuggestions.size > 0 &&
                    selectedSuggestions.size < suggestedMappings.length
                  }
                  checked={
                    selectedSuggestions.size === suggestedMappings.length
                  }
                  onChange={(e) =>
                    setSelectedSuggestions(
                      e.target.checked
                        ? new Set(suggestedMappings.map((s) => s.socketId))
                        : new Set(),
                    )
                  }
                />
              ),
              key: "apply",
              width: 50,
              align: "center" as const,
              render: (_: unknown, record: { socketId: string }) => (
                <Checkbox
                  checked={selectedSuggestions.has(record.socketId)}
                  onChange={(e) => {
                    setSelectedSuggestions((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) {
                        next.add(record.socketId);
                      } else {
                        next.delete(record.socketId);
                      }
                      return next;
                    });
                  }}
                />
              ),
            },
          ]}
        />
      )}
    </div>
  );
}
